/**
 * Color Palette Utility
 *
 * Uses predefined Tailwind CSS v4 color palettes for accurate color rendering.
 * Tailwind palettes use OKLCH color space for perceptually uniform color distribution.
 */

export interface ColorPalette {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

/**
 * Predefined Tailwind CSS v4 color palettes (OKLCH-based)
 * Source: https://tailwindcss.com/docs/customizing-colors
 */
export const DEFAULT_SEMANTIC_COLORS = {
  primary: '#3b82f6', // Blue 500
  success: '#22c55e', // Green 500
  warning: '#f59e0b', // Amber 500
  danger: '#ef4444', // Red 500
  info: '#0ea5e9', // Sky 500
};

const TAILWIND_PALETTES: Record<string, ColorPalette> = {
  // Blue
  '#3b82f6': {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },
  // Red
  '#ef4444': {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },
  // Amber
  '#f59e0b': {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },
  // Green (Emerald)
  '#10b981': {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
    950: '#022c22',
  },
  // Purple (Violet)
  '#8b5cf6': {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7e22ce',
    800: '#6b21a8',
    900: '#581c87',
    950: '#3b0764',
  },
  // Pink
  '#ec4899': {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
    950: '#500724',
  },
  // Cyan
  '#06b6d4': {
    50: '#ecfeff',
    100: '#cffafe',
    200: '#a5f3fc',
    300: '#67e8f9',
    400: '#22d3ee',
    500: '#06b6d4',
    600: '#0891b2',
    700: '#0e7490',
    800: '#155e75',
    900: '#164e63',
    950: '#083344',
  },
  // Orange
  '#f97316': {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
    950: '#431407',
  },
  // Teal
  '#14b8a6': {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
    950: '#042f2e',
  },
  // Indigo
  '#6366f1': {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
    950: '#1e1b4b',
  },
  // Gray
  '#6b7280': {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    950: '#030712',
  },
  // Additional Tailwind colors for better preset support
  // Red variants
  '#dc2626': {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },
  '#f43f5e': {
    // Rose
    50: '#fff1f2',
    100: '#ffe4e6',
    200: '#fecdd3',
    300: '#fda4af',
    400: '#fb7185',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be123c',
    800: '#9f1239',
    900: '#881337',
    950: '#4c0519',
  },
  '#e11d48': {
    // Rose 600
    50: '#fff1f2',
    100: '#ffe4e6',
    200: '#fecdd3',
    300: '#fda4af',
    400: '#fb7185',
    500: '#f43f5e',
    600: '#e11d48',
    700: '#be123c',
    800: '#9f1239',
    900: '#881337',
    950: '#4c0519',
  },
  '#b91c1c': {
    // Red 700
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },
  // Green variants
  '#059669': {
    // Emerald 600
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
    950: '#022c22',
  },
  '#22c55e': {
    // Green
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },
  '#84cc16': {
    // Lime
    50: '#f7fee7',
    100: '#ecfccb',
    200: '#d9f99d',
    300: '#bef264',
    400: '#a3e635',
    500: '#84cc16',
    600: '#65a30d',
    700: '#4d7c0f',
    800: '#3f6212',
    900: '#365314',
    950: '#1a2e05',
  },
  // Orange/Amber variants
  '#fb923c': {
    // Orange 400
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
    950: '#431407',
  },
  '#fbbf24': {
    // Amber 400
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },
  '#eab308': {
    // Yellow
    50: '#fefce8',
    100: '#fef9c3',
    200: '#fef08a',
    300: '#fde047',
    400: '#facc15',
    500: '#eab308',
    600: '#ca8a04',
    700: '#a16207',
    800: '#854d0e',
    900: '#713f12',
    950: '#422006',
  },
  // Blue variants
  '#0ea5e9': {
    // Sky
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
    950: '#082f49',
  },
};

/**
 * Validate hex color format
 */
export function isValidHex(hex: string): boolean {
  return /^#?([a-f\d]{3}|[a-f\d]{6})$/i.test(hex);
}

/**
 * Normalize hex color to lowercase with #
 */
function normalizeHex(hex: string): string {
  let normalized = hex.toLowerCase().startsWith('#') ? hex.toLowerCase() : `#${hex.toLowerCase()}`;
  if (normalized.length === 4) {
    // Expand shorthand hex (e.g. #abc -> #aabbcc)
    normalized =
      '#' +
      normalized[1] +
      normalized[1] +
      normalized[2] +
      normalized[2] +
      normalized[3] +
      normalized[3];
  }
  return normalized;
}

/**
 * Get Tailwind palette for a hex color
 * If exact match exists, use it. Otherwise, find closest palette.
 *
 * @param hex Base color in hex format (e.g., "#3B82F6")
 * @returns ColorPalette object with shades from 50 to 950
 */
export function generatePalette(hex: string): ColorPalette {
  if (!isValidHex(hex)) {
    console.warn(`Invalid hex color: ${hex}. Using default blue palette.`);
    return getDefaultPalette();
  }

  const normalizedHex = normalizeHex(hex);

  // Check for exact match
  if (TAILWIND_PALETTES[normalizedHex]) {
    return TAILWIND_PALETTES[normalizedHex];
  }

  // Generate dynamic palette if no exact match found
  return generateDynamicPalette(hex);
}

/**
 * Semantic Palette Getters
 */
export function getPrimaryPalette(): ColorPalette {
  return TAILWIND_PALETTES[DEFAULT_SEMANTIC_COLORS.primary];
}

export function getSuccessPalette(): ColorPalette {
  return TAILWIND_PALETTES[DEFAULT_SEMANTIC_COLORS.success];
}

export function getWarningPalette(): ColorPalette {
  return TAILWIND_PALETTES[DEFAULT_SEMANTIC_COLORS.warning];
}

export function getDangerPalette(): ColorPalette {
  return TAILWIND_PALETTES[DEFAULT_SEMANTIC_COLORS.danger];
}

export function getInfoPalette(): ColorPalette {
  return TAILWIND_PALETTES[DEFAULT_SEMANTIC_COLORS.info];
}

/**
 * Get default blue palette
 */
export function getDefaultPalette(): ColorPalette {
  return getPrimaryPalette();
}

/**
 * Generate a dynamic palette based on HSL lightness steps
 */
function generateDynamicPalette(hex: string): ColorPalette {
  const { h, s } = hexToHsl(hex);
  return {
    50: hslToHex(h, s, 98),
    100: hslToHex(h, s, 95),
    200: hslToHex(h, s, 90),
    300: hslToHex(h, s, 82),
    400: hslToHex(h, s, 64),
    500: hslToHex(h, s, 50),
    600: hslToHex(h, s, 38),
    700: hslToHex(h, s, 29),
    800: hslToHex(h, s, 15),
    900: hslToHex(h, s, 10),
    950: hslToHex(h, s, 5),
  };
}

/**
 * Convert hex to HSL
 */
function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const normalized = normalizeHex(hex);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized);
  if (!result) {
    return { h: 0, s: 0, l: 0 };
  }
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to Hex
 */
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
